import { DateTime } from '@ksyos/date-time';
import {
    Box,
    Button,
    Col,
    DatePicker,
    FormGroup,
    type GroupedOption,
    Modal,
    Select,
    type SingleValue,
    Text,
    TextArea,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { BrightPlanOperationType } from '@common/interfaces/api/brightPlanApi';
import type { AddEdit } from '@common/utils/utilityTypes';
import type { IEmployment } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils';
import { Profession } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { getDisplayName, type IPerson } from '@common/utils/getDisplayName';
import {
    type BackendBrightPlanScheduleAction,
    BrightPlanScheduleActionSchema,
} from '@common/validation/patientBrightPlanScheduleAction';
import { PatientTaskItemPerformerSelect } from '@frontend/components/pages/patientTasks/PatientTaskItemPerformerSelect';
import { fetchJson } from '@frontend/store/fetch';
import {
    emptyValidationErrors,
    getValidationErrorTranslation,
    updateValidationErrorsForMany,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import { addActionBrightPlan, getLocations, getProfessionals } from '@frontend/utils/handleBrightPlanGetSsoUrl';

type BrightPlanPlanActionModalProps = Readonly<{
    episodeCategoryId: string;
    isFlexibleCarePath: boolean;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => Promise<void>;
    operationTypes: BrightPlanOperationType[];
    patientId: string;
}>;

type BrightPlanSchedulePatientActionAddEdit = Pick<
    AddEdit<BackendBrightPlanScheduleAction>,
    | 'description'
    | 'endDate'
    | 'startDate'
    | 'title'
    | 'poliFollowUpDate'
    | 'performerOrganizationId'
    | 'recipientPractitionerId'
    | 'performerProfession'
> &
    Pick<BackendBrightPlanScheduleAction, 'locationTypeIds' | 'operationTypeIds' | 'professionalIds'>;

export function BrightPlanPlanActionModal({
    episodeCategoryId,
    isFlexibleCarePath,
    isOpen,
    onClose,
    onSave,
    operationTypes,
    patientId,
}: BrightPlanPlanActionModalProps) {
    const translate = getLanguage();
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const practitionerProfession = useSelector((state: IReduxState) => state.authentication.profession);
    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const initialScheduleAction = {
        creatorPractitionerId: practitionerId,
        description: null,
        endDate: null,
        episodeCategoryId,
        locationTypeIds: [],
        operationTypeIds: [],
        performerOrganizationId: organizationId,
        performerProfession: practitionerProfession,
        poliFollowUpDate: null,
        professionalIds: [],
        startDate: null,
        title: null,
    };

    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [scheduleAction, setScheduleAction] = useState<BrightPlanSchedulePatientActionAddEdit>(initialScheduleAction);
    const [locationOptions, setLocationOptions] = useState<{ label: string; value: number }[]>([]);
    const [professionalOptions, setProfessionalOptions] = useState<{ label: string; value: string }[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [employments, setEmployments] = useState<IEmployment[]>([]);
    const [performerPractitionerOptions, setPerformerPractitionerOptions] = useState<GroupedOption[]>([]);

    useEffect(() => {
        if (isOpen) {
            loadPractitioners();
        }
    }, [isOpen]);

    const operationTypeOptions = operationTypes.map((operationType) => ({
        label: operationType.Name,
        value: operationType.Id,
    }));

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => onClose()}
            size="large"
        >
            <Modal.Header>Uitnodiging</Modal.Header>
            <Modal.Body>
                <FormGroup
                    hasErrors={errors.has('title')}
                    isRequired={true}
                    label="Titel"
                >
                    <TextInput
                        error={getValidationErrorTranslation(errors.get('title'))}
                        onChange={(e) => handleChange('title', e.target.value ? e.target.value : null)}
                        value={scheduleAction.title ?? ''}
                    />
                </FormGroup>
                <FormGroup
                    hasErrors={errors.has('description')}
                    label="Beschrijving"
                >
                    <TextArea
                        error={getValidationErrorTranslation(errors.get('description'))}
                        onChange={(e) => handleChange('description', e.target.value ? e.target.value : null)}
                        value={scheduleAction.description ?? ''}
                    />
                </FormGroup>
                <FormGroup
                    hasErrors={errors.has('operationTypeIds')}
                    isRequired={true}
                    label="Verrichting"
                >
                    <Select
                        error={getValidationErrorTranslation(errors.get('operationTypeIds'))}
                        isClearable={true}
                        onChange={async (item) => {
                            handleChange('operationTypeIds', item ? [item.value] : []);
                        }}
                        options={operationTypeOptions}
                        value={
                            operationTypeOptions.find(
                                (operationType) => operationType.value === scheduleAction.operationTypeIds?.[0]
                            ) ?? null
                        }
                    />
                </FormGroup>
                <FormGroup
                    hasErrors={errors.has('locationTypeIds')}
                    isRequired={true}
                    label="Locatie"
                >
                    <Select
                        closeMenuOnSelect={false}
                        error={getValidationErrorTranslation(errors.get('locationTypeIds'))}
                        isClearable={true}
                        isMulti
                        onChange={(items) =>
                            handleChange(
                                'locationTypeIds',
                                items.map((location) => location.value)
                            )
                        }
                        options={locationOptions}
                        value={locationOptions.filter((location) =>
                            scheduleAction.locationTypeIds?.includes(location.value)
                        )}
                    />
                </FormGroup>
                <FormGroup
                    hasErrors={errors.has('professionalIds')}
                    isRequired={true}
                    label="Specialist"
                >
                    <Select
                        closeMenuOnSelect={false}
                        error={getValidationErrorTranslation(errors.get('professionalIds'))}
                        isClearable={true}
                        isMulti
                        noOptionsMessage={() => ''}
                        onChange={(items) =>
                            handleChange(
                                'professionalIds',
                                items.map((professional) => professional.value)
                            )
                        }
                        options={professionalOptions}
                        value={professionalOptions.filter((professional) =>
                            scheduleAction.professionalIds?.includes(professional.value)
                        )}
                    />
                </FormGroup>
                <FormGroup
                    hasErrors={errors.has('startDate') || errors.has('endDate')}
                    isRequired={true}
                    label="Afspraakperiode"
                >
                    <Col span={[12, 8, 6]}>
                        <Box
                            display="flex"
                            flexDirection={['column', 'row']}
                            gap={2}
                        >
                            <Box>
                                <DatePicker
                                    error={getValidationErrorTranslation(errors.get('startDate'))}
                                    minValue={DateTime.now().toDate()}
                                    onChange={(value) =>
                                        handleChange(
                                            'startDate',
                                            value ? DateTime.from(value).format('YYYY-MM-DD') : null
                                        )
                                    }
                                    value={
                                        scheduleAction.startDate
                                            ? DateTime.from(scheduleAction.startDate).toDate()
                                            : null
                                    }
                                />
                            </Box>
                            <Text
                                as="label"
                                pt={2.5}
                                size="xs"
                            >
                                t/m
                            </Text>
                            <Box>
                                <DatePicker
                                    error={getValidationErrorTranslation(errors.get('endDate'))}
                                    minValue={DateTime.now().toDate()}
                                    onChange={(value) =>
                                        handleChange(
                                            'endDate',
                                            value ? DateTime.from(value).format('YYYY-MM-DD') : null
                                        )
                                    }
                                    value={
                                        scheduleAction.endDate ? DateTime.from(scheduleAction.endDate).toDate() : null
                                    }
                                />
                            </Box>
                        </Box>
                    </Col>
                </FormGroup>
                <FormGroup
                    hasErrors={errors.has('performerProfession') || errors.has('recipientPractitionerId')}
                    label={translate.contactsAndTasks.performer}
                >
                    <PatientTaskItemPerformerSelect
                        error={errors.get('recipientPractitionerId') ?? errors.get('performerProfession')}
                        id="performer"
                        onChange={(selectedOption) => performerOnChange(selectedOption)}
                        options={performerPractitionerOptions}
                        value={
                            performerPractitionerOptions.find(
                                (item) => item.value === scheduleAction.recipientPractitionerId
                            ) ||
                            performerPractitionerOptions.find(
                                (item) => item.value === scheduleAction.performerProfession
                            )
                        }
                    />
                </FormGroup>
                <FormGroup
                    hasErrors={errors.has('poliFollowUpDate')}
                    isRequired={true}
                    label="Uiterlijke reactie datum"
                >
                    <DatePicker
                        error={getValidationErrorTranslation(errors.get('poliFollowUpDate'))}
                        minValue={DateTime.now().toDate()}
                        onChange={(value) =>
                            handleChange('poliFollowUpDate', value ? DateTime.from(value).format('YYYY-MM-DD') : null)
                        }
                        value={
                            scheduleAction.poliFollowUpDate
                                ? DateTime.from(scheduleAction.poliFollowUpDate).toDate()
                                : null
                        }
                    />
                </FormGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={() => {
                        setScheduleAction(initialScheduleAction);
                        setErrors(emptyValidationErrors);
                        onClose();
                    }}
                    variant="outline"
                >
                    {translate.common.cancel}
                </Button>
                <Button
                    isDisabled={errors.size > 0 || isSaving}
                    onClick={async () => {
                        setIsSaving(true);
                        const { errors: validationErrors, item } = validate(
                            BrightPlanScheduleActionSchema,
                            scheduleAction
                        );
                        if (validationErrors.size > 0 || !item) {
                            setErrors(validationErrors);
                            setIsSaving(false);

                            return;
                        }
                        await addActionBrightPlan({
                            episodeCategoryId,
                            isFlexibleCarePath,
                            patientId,
                            scheduleAction: item,
                        });
                        await onSave();
                        onClose();
                        setScheduleAction(initialScheduleAction);
                        setErrors(emptyValidationErrors);
                        setIsSaving(false);
                    }}
                >
                    {translate.support.send}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    async function loadPractitioners() {
        const result = await fetchJson(
            `/api/employments/${organizationId}/practitioners?isActive=true&taskEpisodeCategory=${episodeCategoryId}`,
            'GET'
        );
        const results = result.json.items;
        const options = getEmploymentPerformerPractitionerOptions(results);
        setEmployments(results);
        setPerformerPractitionerOptions(options);
    }

    function getEmploymentPerformerPractitionerOptions(fetchedEmployments: IEmployment[]) {
        const professions = [
            ...new Set<Profession | null>(fetchedEmployments.map((employment) => employment.profession)),
        ] as Profession[];
        const options: GroupedOption[] = [];

        for (const profession of professions) {
            options.push({
                isGroupHeader: true,
                label: translate.professionEnum[profession as keyof typeof translate.professionEnum],
                value: profession,
            });
            const filteredServices = fetchedEmployments.filter((employment) => employment.profession === profession);
            filteredServices.forEach((employment) =>
                options.push({
                    isGroupOption: true,
                    label: getDisplayName(employment.practitioner as IPerson),
                    value: employment.practitioner?.id as string,
                })
            );
        }

        return options;
    }

    async function performerOnChange(selectedOption: SingleValue<GroupedOption>) {
        const updatedScheduleAction = { ...scheduleAction };

        if (!selectedOption?.value) {
            updatedScheduleAction.performerOrganizationId = organizationId;
            delete updatedScheduleAction.recipientPractitionerId;
            updatedScheduleAction.performerProfession = practitionerProfession;
        } else {
            if (Object.keys(Profession).includes(selectedOption.value)) {
                updatedScheduleAction.performerProfession = selectedOption.value as Profession;
                delete updatedScheduleAction.recipientPractitionerId;
                updatedScheduleAction.performerOrganizationId = organizationId;
            } else {
                updatedScheduleAction.performerOrganizationId = organizationId;
                updatedScheduleAction.recipientPractitionerId = selectedOption.value;
                updatedScheduleAction.performerProfession = employments.find(
                    (employment) => employment.practitioner?.id === selectedOption.value
                )?.profession as Profession;
            }

            const updatedErrors = updateValidationErrorsForMany(
                BrightPlanScheduleActionSchema,
                updatedScheduleAction,
                errors,
                ['performerProfession', 'recipientPractitionerId']
            );

            setScheduleAction(updatedScheduleAction);
            setErrors(updatedErrors);
        }
    }

    async function getLocationTypes(operationTypes: string[]) {
        if (!operationTypes.length) {
            setLocationOptions([]);

            return [];
        }
        const locationTypes = await getLocations(operationTypes);
        const locationOptionResult = locationTypes.map((location) => ({
            label: location.Name,
            value: location.Id,
        }));
        setLocationOptions(locationOptionResult);

        return locationOptionResult;
    }

    async function getProfessionalIds(operationTypes: string[], locations: number[]) {
        if (!operationTypes.length || !locations.length) {
            setProfessionalOptions([]);

            return [];
        }
        const professionals = await getProfessionals(operationTypes, locations);
        const professionalOptionsResult = professionals.map((professional) => ({
            label: professional.name,
            value: professional.id,
        }));
        setProfessionalOptions(professionalOptionsResult);

        return professionalOptionsResult;
    }

    async function handleChange<K extends keyof BrightPlanSchedulePatientActionAddEdit>(
        key: K,
        value: BrightPlanSchedulePatientActionAddEdit[K]
    ) {
        const updatedScheduleAction = { ...scheduleAction, [key]: value };

        let updatedErrors = updateValidationErrorsForOne(
            BrightPlanScheduleActionSchema,
            updatedScheduleAction,
            errors,
            key
        );

        if (
            ['startDate', 'endDate'].includes(key) &&
            updatedScheduleAction.startDate &&
            updatedScheduleAction.endDate
        ) {
            updatedErrors = updateValidationErrorsForMany(
                BrightPlanScheduleActionSchema,
                updatedScheduleAction,
                updatedErrors,
                ['startDate', 'endDate']
            );
        }

        if (key === 'operationTypeIds') {
            const locationTypes = await getLocationTypes(updatedScheduleAction.operationTypeIds);
            const locationTypeIds = locationTypes.map((location) => location.value);
            const professionals = await getProfessionalIds(updatedScheduleAction.operationTypeIds, locationTypeIds);

            updatedScheduleAction.locationTypeIds = locationTypeIds;
            updatedScheduleAction.professionalIds = professionals.map((professional) => professional.value);

            updatedErrors = updateValidationErrorsForMany(
                BrightPlanScheduleActionSchema,
                updatedScheduleAction,
                updatedErrors,
                ['professionalIds', 'locationTypeIds']
            );
        } else if (key === 'locationTypeIds') {
            const professionals = await getProfessionalIds(
                updatedScheduleAction.operationTypeIds,
                updatedScheduleAction.locationTypeIds
            );
            updatedScheduleAction.professionalIds = professionals.map((professional) => professional.value);
            updatedErrors = updateValidationErrorsForOne(
                BrightPlanScheduleActionSchema,
                updatedScheduleAction,
                updatedErrors,
                'professionalIds'
            );
        }
        setScheduleAction(updatedScheduleAction);
        setErrors(updatedErrors);
    }
}
